<div class="table-wrapper" #tableRef>
  <div class="table-scroll-container">
    <table class="htx-table">
      @if (sortedData().length > 0) {
        <thead>
          <tr class="table-header-row">
            @for (column of columns(); track column.name) {
              <th
                class="table-header-cell"
                [style.width]="column.width"
                [class.table-header-cell--sortable]="column.sortable"
                [class.table-header-cell--filterable]="column.filterable"
                (click)="column.sortable && onHeaderClick(column)"
              >
                <div class="header-content">
                  <div class="header-label">
                    <span>{{ column.headerLabel }}</span>
                  </div>
                  <div class="header-controls">
                    @if (column.sortable) {
                      <span class="sort-icon">
                        @if (isSortActive(column.name)) {
                          <htx-icon
                            class="icon-active"
                            [icon]="
                              getSortDirection(column.name) === 'asc'
                                ? 'arrow_upward'
                                : 'arrow_downward'
                            "
                          ></htx-icon>
                        } @else {
                          <htx-icon class="icon-inactive" [icon]="'unfold_more'"></htx-icon>
                        }
                      </span>
                    }
                    @if (column.filterable) {
                      <div
                        class="filter-popup-trigger"
                        (click)="toggleFilterPopup(column.name, $event)"
                      >
                        <htx-icon
                          class="filter-icon"
                          [icon]="filterValues()[column.name] ? 'filter_list' : 'filter_list_off'"
                        ></htx-icon>
                      </div>
                    }
                  </div>
                </div>
              </th>
            }
          </tr>
        </thead>
      }
      <tbody>
        @if (loading()) {
          <tr class="table-skeleton-row">
            <td [attr.colspan]="columns().length" class="table-skeleton-cell">
              <htx-skeleton
                width="100%"
                height="2.75rem"
                [lines]="5"
                gap="0.25rem"
                borderRadius="0"
              ></htx-skeleton>
            </td>
          </tr>
        } @else {
          @for (row of paginatedData(); track row[rowIdField()]; let last = $last) {
            <tr
              class="table-body-row"
              [class.table-body-row--last]="last"
              (click)="onRowClick(row)"
            >
              @for (column of columns(); track column.name) {
                <td class="table-cell" [ngClass]="getCellClass(column.columnType)">
                  @if (column.columnType === 'plain') {
                    <span class="table-cell__content">{{ getCellContent(row, column) }}</span>
                  } @else if (column.columnType === 'date') {
                    <span class="table-cell__content">{{
                      formatDate($any(row)[column.name])
                    }}</span>
                  } @else if (column.columnType === 'template') {
                    <div class="table-cell__template">
                      @if (column.template) {
                        <ng-container
                          *ngTemplateOutlet="column.template; context: { $implicit: row }"
                        ></ng-container>
                      }
                    </div>
                  }
                </td>
              }
            </tr>
          }
          @if (paginatedData().length === 0) {
            <tr class="table-empty-row">
              <td [attr.colspan]="columns().length" class="table-empty-cell">
                <span class="table-empty-text">Keine Daten verfügbar</span>
              </td>
            </tr>
          }
        }
      </tbody>
    </table>
  </div>

  <!-- Pagination -->
  @if (enablePagination() && (totalPages() > 1 || totalItems() !== null)) {
    <div class="table-pagination">
      <div class="pagination-info">
        <span class="pagination-info__text">
          {{ currentPageIndex() * pageSizeSignal() + 1 }} bis
          {{
            Math.min(
              (currentPageIndex() + 1) * pageSizeSignal(),
              totalItems() ?? sortedData().length
            )
          }}
          von {{ totalItems() ?? sortedData().length }}
        </span>
        <span class="pagination-info__separator">|</span>
        <select
          class="pagination-page-size"
          [value]="pageSizeSignal()"
          (change)="onPageSizeChange(+$any($event.target).value)"
        >
          @for (size of pageSizeOptions(); track size) {
            <option [value]="size">{{ size }} pro Seite</option>
          }
        </select>
      </div>
      <div class="pagination-controls">
        <button
          class="pagination-btn pagination-btn--nav"
          [disabled]="currentPageIndex() === 0"
          (click)="previousPage()"
          title="Vorherige Seite"
        >
          <htx-icon [icon]="'chevron_left'"></htx-icon>
        </button>

        @if (pageNumbers()[0] > 0) {
          <button
            class="pagination-btn pagination-page"
            (click)="goToPage(0)"
            title="Zur ersten Seite"
          >
            1
          </button>
          @if (pageNumbers()[0] > 1) {
            <span class="pagination-ellipsis">...</span>
          }
        }

        @for (page of pageNumbers(); track page) {
          <button
            class="pagination-btn pagination-page"
            [class.active]="page === currentPageIndex()"
            (click)="goToPage(page)"
            [title]="'Zur Seite ' + (page + 1)"
          >
            {{ page + 1 }}
          </button>
        }

        @if (pageNumbers()[pageNumbers().length - 1] < totalPages() - 1) {
          @if (pageNumbers()[pageNumbers().length - 1] < totalPages() - 2) {
            <span class="pagination-ellipsis">...</span>
          }
          <button
            class="pagination-btn pagination-page"
            (click)="goToPage(totalPages() - 1)"
            [title]="'Zur Seite ' + totalPages()"
          >
            {{ totalPages() }}
          </button>
        }

        <button
          class="pagination-btn pagination-btn--nav"
          [disabled]="currentPageIndex() === totalPages() - 1"
          (click)="nextPage()"
          title="Nächste Seite"
        >
          <htx-icon [icon]="'chevron_right'"></htx-icon>
        </button>
      </div>
    </div>
  }
</div>

<!-- Filter Popup (fixed positioned) -->
@if (activeFilterColumn() && filterPopupPosition() && activeFilterColumnConfig()) {
  <div
    class="filter-popup-fixed"
    [style.top.px]="filterPopupPosition()!.top"
    [style.left.px]="filterPopupPosition()!.left"
    (click)="$event.stopPropagation()"
  >
    @if (
      activeFilterColumnConfig()!.filterOptions &&
      activeFilterColumnConfig()!.filterOptions!.length > 0
    ) {
      <!-- Dropdown filter for enum-based columns -->
      <select
        [value]="filterValues()[activeFilterColumn()!] || ''"
        (change)="onFilterChange(activeFilterColumn()!, $any($event.target).value)"
        class="filter-select"
      >
        <option value="">-- Alle --</option>
        @for (option of activeFilterColumnConfig()!.filterOptions; track option.value) {
          <option [value]="option.value">{{ option.label }}</option>
        }
      </select>
    } @else {
      <!-- Text input filter for other columns -->
      <input
        type="text"
        [placeholder]="'Filter ' + activeFilterColumnConfig()!.headerLabel"
        [value]="filterValues()[activeFilterColumn()!] || ''"
        (input)="onFilterChange(activeFilterColumn()!, $any($event.target).value)"
        class="filter-input"
        (keydown.escape)="closeFilterPopup()"
      />
    }
    <div class="filter-popup-actions">
      <button class="filter-btn-clear" (click)="onFilterChange(activeFilterColumn()!, '')">
        Löschen
      </button>
      <button class="filter-btn-close" (click)="closeFilterPopup()">Schließen</button>
    </div>
  </div>
}
